import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { showApixErrorMessage } from "utilities/api/apix-handler";

import { contextStepUpStatusGetV2 } from "handlers/context/context";
import { type StepUpStatus } from "handlers/models";

import { BTSessionStorage } from "../../types/btStorage";
import { STEP_UP_MAX_AGE_MINUTES } from "./constants";

export interface IStepUpAuthContextType {
    hasStepUp: boolean;
    isChecking: boolean;
    stepUpStatus: StepUpStatus | null;
    hasError: boolean;
    checkStepUp: (force?: boolean) => Promise<{ isValid: boolean; hasError: boolean }>;
    triggerStepUp: (returnUrl?: string) => void;
    clearStepUp: () => void;
}

const StepUpAuthContext = createContext<IStepUpAuthContextType | undefined>(undefined);

export interface IStepUpAuthProviderProps {
    children: ReactNode;
}

export const StepUpAuthProvider: React.FC<IStepUpAuthProviderProps> = ({ children }) => {
    const [hasStepUp, setHasStepUp] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [stepUpStatus, setStepUpStatus] = useState<StepUpStatus | null>(null);
    const [hasError, setHasError] = useState(false);

    // Check step-up status (no caching for single-use security)
    const checkStepUp = useCallback(async (): Promise<{ isValid: boolean; hasError: boolean }> => {
        setIsChecking(true);
        setHasError(false);

        try {
            // Always fetch from backend (no caching) because step-up is single-use
            // The backend invalidates the step-up claims after each successful use
            const response = await contextStepUpStatusGetV2({
                maxAgeMinutes: STEP_UP_MAX_AGE_MINUTES,
            });
            const status = response.data;

            if (status) {
                const hasValidStepUp = (status.hasStepUp ?? false) && !(status.isExpired ?? true);
                setHasStepUp(hasValidStepUp);
                setStepUpStatus(status);

                // Clear redirect tracking if step-up authentication succeeded
                if (hasValidStepUp) {
                    BTSessionStorage.remove("bt-object-stepUpContext");
                }

                return { isValid: hasValidStepUp, hasError: false };
            }

            return { isValid: false, hasError: false };
        } catch (error) {
            // Log the error for debugging purposes
            showApixErrorMessage(error as Error, "Step-up authentication check failed");
            setHasError(true);
            return { isValid: false, hasError: true };
        } finally {
            setIsChecking(false);
        }
    }, []);

    // Trigger step-up authentication (legacy mode - redirects current window)
    // For popup mode, use useStepUpPopup hook instead
    const triggerStepUp = useCallback((returnUrl?: string) => {
        const targetUrl = returnUrl ?? window.location.pathname + window.location.search;
        const stepUpUrl = `/api/login/LoginWithAuth0?stepUpReturnUrl=${encodeURIComponent(
            targetUrl,
        )}`;
        window.location.assign(stepUpUrl);
    }, []);

    // Clear step-up status
    const clearStepUp = useCallback(() => {
        setHasStepUp(false);
        setStepUpStatus(null);
        setHasError(false);
    }, []);

    const value: IStepUpAuthContextType = useMemo(
        () => ({
            hasStepUp,
            isChecking,
            stepUpStatus,
            hasError,
            checkStepUp,
            triggerStepUp,
            clearStepUp,
        }),
        [hasStepUp, isChecking, stepUpStatus, hasError, checkStepUp, triggerStepUp, clearStepUp],
    );

    return <StepUpAuthContext.Provider value={value}>{children}</StepUpAuthContext.Provider>;
};

export const useStepUpAuth = (): IStepUpAuthContextType => {
    const context = useContext(StepUpAuthContext);
    if (!context) {
        throw new Error("useStepUpAuth must be used within a StepUpAuthProvider");
    }
    return context;
};
